import BookmarkPlusDefaultIcon from '@/assets/images/icons/bookmark-plus-default.svg';
import BookmarkPlusHoverIcon from '@/assets/images/icons/bookmark-plus-hover.svg';
import BookmarkPlusPressedIcon from '@/assets/images/icons/bookmark-plus-pressed.svg';
import { AudioMaterialItem } from '@/components/AudioMaterialItem';
import { BookmarkSave } from '@/components/BookmarkSave';
import { BreadcrumbHeader } from '@/components/BreadcrumbHeader';
import { CoursePagePremiumLoader } from '@/components/course/CoursePagePremiumLoader';
import { LessonCourseSidebar, SIDEBAR_SHELL_PADDING_TOP } from '@/components/lesson/LessonCourseSidebar';
import {
  lessonNavCompactSurface,
  lessonNavForeground,
} from '@/components/lesson/lessonNavButtonStyles';
import { LessonPageTopBar } from '@/components/lesson/LessonPageTopBar';
import DownloadButton from '@/components/lesson/DownloadButton';
import LessonPlayer, { type LessonPlayerRef } from '@/components/lesson/LessonPlayer';
import LessonPlayerOffline from '@/components/lesson/LessonPlayer/Offline';
import { LessonShareModal } from '@/components/lesson/LessonShareModal';
import { LessonSidebarPanel } from '@/components/lesson/LessonSidebarPanel';
import { MarkCompleteButton } from '@/components/lesson/MarkCompleteButton';
import { MarkCompleteToolbarIcon } from '@/components/lesson/MarkCompleteToolbarIcon';
import { LinkMaterialItem, PdfMaterialItem, renderMaterialIcon } from '@/components/lesson/MaterialItems';
import { ShortcutsTabContent } from '@/components/lesson/ShortcutsTabContent';
import { AccessDenied, ContentWrapper, ErrorDisplay, StatusBadge, useErrorHandler } from '@/components/ui';
import { LessonCompletionModal } from '@/components/ui/LessonCompletionModal';
import { LessonCompletionSheet } from '@/components/ui/LessonCompletionSheet';
import { Sheet, SheetDragHandle, SheetScrollView } from '@/components/ui/Sheet';
import { Tooltip } from '@/components/ui/Tooltip';
import { UpNextOverlay } from '@/components/ui/UpNextOverlay';
import VideoActionButtons, { NotationMode } from '@/components/VideoActionsButton';
import { formatPageTitle, useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useDraftViewer } from '@/hooks/useDraftViewer';
import { useLessonSidebarExpanded } from '@/hooks/useLessonSidebarExpanded';
import { useMaterialDownload } from '@/hooks/useMaterialDownload';
import { User, useSoundsliceTracker } from '@/hooks/useSoundsliceTracker';
import { URLS } from '@/lib/constants';
import { DASHBOARD_TOOLBAR_ARTWORK_PX } from '@/lib/dashboardToolbarUi';
import { NetworkError, PermissionDeniedError, ResourceNotFoundError, UnknownApplicationError } from '@/lib/errors';
import {
  applyQueuedProgress,
  decidePlaybackSource,
  enqueueCompleted,
  enqueueLeftOff,
  getIsOnline,
  getOfflinePlayability,
  getQueuedProgress,
  loadLessonSnapshot,
  offlineFileExists,
  offlineFileUri,
  parseVtt,
  playabilityMessage,
  readOfflineText,
  useDownload,
  useIsOnline,
  usePlaybackPreference,
  type CaptionCue,
  type PlaybackSource,
} from '@/lib/offline';
import { logger } from '@/lib/logger';
import { isMemberTier } from '@/lib/membershipTier';
import { getCustomerInfo, hasActiveEntitlement } from '@/lib/purchases/RevenueCat';
import { getProxiedMediaUrl } from '@/lib/media-proxy';
import { signAudioMaterialUrls } from '@/lib/signedFiles';
import { PdfViewerModal } from '@/components/PdfViewerModal';
import { setPlayerFocusActive } from '@/lib/playerFocusStore';
import { CATALOG_STALE_TIME } from '@/lib/queryClient';
import { useCurrentUrl } from '@/lib/useCurrentPath';
import { useIsLandscapeMobile, useIsMobile } from '@/lib/useDeviceType';
import { gaService } from '@/services/ga.service';
import { mixpanelService } from '@/services/mixpanel.service';
import { wpAuth } from '@/services/wordpress-auth.service';
import { fetchLessonSidebar } from '@/supabase/services/lessonSidebar';
import { fetchLessonVideosWithProgress } from '@/supabase/services/lessonVideosWithProgress';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useNavigation } from '@react-navigation/native';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Haptics from 'expo-haptics';
import { useGlobalSearchParams, useLocalSearchParams, useRouter } from 'expo-router';
import { setStatusBarHidden } from 'expo-status-bar';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  BackHandler,
  Linking,
  Platform,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Button, SizableText, XStack, YStack } from 'tamagui';
import { BodyText, ContentTitle, SubtitleText } from '@/components/ui/Typography';
import { useAuth } from '@/lib/AuthContext';
import {
  buildLessonShareUrlArgs,
  deepLinkSeekSecondsForCurrentVideo,
  normalizeRouteQueryString,
  parseNumericCollectionId,
  pickInitialLessonVideo,
} from '@/lib/lesson-deep-link';
import {
  fetchLessonPageData,
  getLessonNavigation,
  markLessonOpened
} from '@/supabase/services';
import type { LessonPageData } from '@/supabase/services/lessonOptimized';
import { markVideoCompleted, updateVideoLeftOffTime } from '@/supabase/services/userVideoInteractions';
import { Lesson, LessonMaterial, LessonVideo, LessonVideoChapter, UserLessonVideoInteraction } from '@/types';
import { buildAbsoluteLessonShareUrl, buildLessonUrl } from '@/utils/buildLessonUrl';
import { getLessonDisplayTitle } from '@/utils/lessonStructuredTitle';
import { areAllVideosCompleted, getUserProgressForVideo } from '@/utils/lessonVideoHelpers';


const MOBILE_PLAYER_MIN_HEIGHT = 400;
/** Caps lesson main column (video + tabs) on wide desktop/tablet web. */
const LESSON_DESKTOP_CONTENT_MAX_WIDTH = 1100;
/** Height of the slim control strip shown above the player in focus mode. */
const FOCUS_BAR_H = 48;
/** Horizontal padding applied by the mobile ContentWrapper; cancelled in focus. */
const MOBILE_CONTENT_PAD_X = 16;
/** Max content column width on the mobile layout (matches the ContentWrapper). */
const MOBILE_CONTENT_MAX_WIDTH = 1140;
/** SoundSlice's transport/control bar height reserved below the video frame. */
const SOUNDSLICE_TRANSPORT_H = 56;

function getMobilePlayerHeight(contentWidth: number, screenHeight: number): number {
  const video16x9 = Math.round(contentWidth * (9 / 16));
  const base = video16x9 + SOUNDSLICE_TRANSPORT_H;
  // One height for both video-only and notation views so toggling notation never
  // resizes the player (the resize used to fight the notation command and made
  // the toggle need several taps). Fill a comfortable share of the portrait
  // viewport, scaling with device height (capped), but never below 16:9 + bar.
  const portraitTarget = Math.min(Math.round(screenHeight * 0.6), 540);
  return Math.max(base, portraitTarget, MOBILE_PLAYER_MIN_HEIGHT);
}

type VideoPlayerParams = {
  "lesson-slug": string;
  "course-slug": string;
  referrer: string;
  referrerTitle?: string;
  courseTitle?: string;
};

// Native only: Soundslice decides desktop vs mobile layout with
// `"1" === params.fs || !/Android|iPhone|iPad/.test(userAgent)`. The native
// WebView now sends a real iPhone user agent (so Soundslice uses iOS-native
// HLS instead of hls.js/MediaSource, which drops frames in WKWebView), so the
// desktop layout the app was built around has to be requested explicitly.
// `forcedesktop=1` is not a Soundslice parameter; it is kept only because
// existing URLs carry it. Web keeps its current behaviour untouched.
const NATIVE_LAYOUT_PARAM = Platform.OS === 'web' ? '' : '&fs=1';

const getEmbedUrl = (url: string, userId?: string) => {
  if (url.includes('/embed/')) {
    let finalUrl = url;
    if (!finalUrl.includes('api=1')) finalUrl += `${finalUrl.includes('?') ? '&' : '?'}api=1`;
    if (!finalUrl.includes('responsive=1')) finalUrl += `&responsive=1`;
    if (!finalUrl.includes('forcedesktop=1')) finalUrl += `&forcedesktop=1`;
    if (NATIVE_LAYOUT_PARAM && !finalUrl.includes('fs=1')) finalUrl += NATIVE_LAYOUT_PARAM;
    if (userId && !finalUrl.includes('u=')) finalUrl += `&u=${userId}`;
    return finalUrl;
  }
  const sliceIdMatch = url.match(/slices\/([^/]+)/);
  if (sliceIdMatch && sliceIdMatch[1]) {
    const sliceId = sliceIdMatch[1];
    const userParam = userId ? `&u=${userId}` : '';
    return `https://www.soundslice.com/slices/${sliceId}/embed/?api=1&responsive=1&forcedesktop=1${NATIVE_LAYOUT_PARAM}${userParam}`;
  }
  return null;
};

const COPY_LINK_TOAST_MS = 2500;
const COPY_TOAST_BOTTOM_OFFSET = 20;
const COPY_TOAST_MIN_SAFE_INSET = 12;
const COPY_TOAST_Z_INDEX = 20000;
const SEEK_AFTER_VIDEO_CHANGE_MS_WEB = 1500;
const SEEK_AFTER_VIDEO_CHANGE_MS_NATIVE = 1000;

export default function VideoPlayerScreen() {
  const { "lesson-slug": lessonSlug, 
          "course-slug": courseSlug, 
          referrer, referrerTitle, courseTitle: courseTitleParam } = useLocalSearchParams<VideoPlayerParams>();

  const router = useRouter();
  const { fullPath } = useCurrentUrl();
  const globalParams = useGlobalSearchParams<{ t?: string; video?: string; collectionId?: string }>();
  const routeCourseSlug = useMemo(() => normalizeRouteQueryString(courseSlug), [courseSlug]);

  const [activeLessonSlug, setActiveLessonSlug] = useState<string>(() =>
    normalizeRouteQueryString(lessonSlug),
  );
  const urlVideoParam = useMemo(
    () => normalizeRouteQueryString(globalParams.video),
    [globalParams.video],
  );
  const { user, membershipTier, refreshMembership } = useAuth();
  const [appleMember, setAppleMember] = useState(false);
  const canViewDrafts = useDraftViewer();
  const { showError, showSuccess } = useErrorHandler();

  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [playerLoading, setPlayerLoading] = useState(true);
  // Offline playback: the lesson was hydrated from a local snapshot and the
  // current video plays from its downloaded file (native only).
  const [offlinePlayback, setOfflinePlayback] = useState(false);
  const offlinePlaybackRef = useRef(false);
  useEffect(() => { offlinePlaybackRef.current = offlinePlayback; }, [offlinePlayback]);
  const [pageData, setPageData] = useState<LessonPageData | null>(null);
  const [offlineCaptionCues, setOfflineCaptionCues] = useState<CaptionCue[]>([]);
  const isOnline = useIsOnline();
  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef<LessonPlayerRef>(null);
  const lastSavedTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!(error instanceof PermissionDeniedError)) return;
    let alive = true;
    void getCustomerInfo().then((info) => {
      if (alive) setAppleMember(hasActiveEntitlement(info));
    });
    return () => {
      alive = false;
    };
  }, [user?.id, error]);
  
  const [activeTab, setActiveTab] = useState<'overview' | 'materials' | 'shortcuts'>('overview');
  
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [lessonSummary, setLessonSummary] = useState<string | null>(null);
  const displayLessonTitle = useMemo(
    () => (lesson ? getLessonDisplayTitle(lesson) : null),
    [lesson],
  );
  const [videos, setVideos] = useState<LessonVideo[]>([]);
  
  const [userVideos, setUserVideos] = useState<UserLessonVideoInteraction[]>([]);
  const [lessonVideosByLessonId, setLessonVideosByLessonId] = useState<Record<number, LessonVideo[]>>({});
  const [userVideosByLessonId, setUserVideosByLessonId] = useState<Record<number, UserLessonVideoInteraction[]>>({});
  const [siblingLoadingLessonIds, setSiblingLoadingLessonIds] = useState<number[]>([]);
  const [currentVideo, setCurrentVideo] = useState<LessonVideo | null>(null);
  const offlineRecord = useDownload(currentVideo?.id);
  const offlinePlayability = useMemo(
    () => getOfflinePlayability(offlineRecord, user?.id),
    [offlineRecord, user?.id],
  );
  // Online with a downloaded copy the user picks the player: Soundslice
  // (stream) or the downloaded file. Offline it is always the file.
  const playbackPreference = usePlaybackPreference();
  // What the user picked, and the lesson it was picked in: the choice covers
  // that lesson's other downloaded videos and ends when the lesson changes.
  const [visitPlaybackChoice, setVisitPlaybackChoice] = useState<{ lessonId: number | null; source: PlaybackSource } | null>(null);
  const hasPlayableDownload = useMemo(
    () =>
      Platform.OS !== 'web' &&
      !!offlineRecord &&
      offlinePlayability.playable &&
      offlineFileExists(offlineRecord.videoPath),
    [offlineRecord, offlinePlayability.playable],
  );
  // Whether the download already existed when this video was opened. Set
  // during render so the first pass for a new video already knows.
  const [openedVideo, setOpenedVideo] = useState<{ id: number | null; downloaded: boolean }>({
    id: null,
    downloaded: false,
  });
  if (currentVideo && openedVideo.id !== currentVideo.id) {
    setOpenedVideo({ id: currentVideo.id, downloaded: hasPlayableDownload });
  }
  const playbackDecision = decidePlaybackSource({
    offline: offlinePlayback,
    downloaded: hasPlayableDownload,
    downloadedAtOpen: openedVideo.id === currentVideo?.id ? openedVideo.downloaded : hasPlayableDownload,
    visitChoice:
      visitPlaybackChoice && visitPlaybackChoice.lessonId === (currentVideo?.lesson_id ?? null)
        ? visitPlaybackChoice.source
        : null,
    preference: playbackPreference,
  });
  // The player is the downloaded file (offline, or chosen while online).
  const localPlayback = playbackDecision === 'download';
  const offlineVideoUri = useMemo(
    () => (localPlayback && offlineRecord && offlinePlayability.playable ? offlineFileUri(offlineRecord.videoPath) : null),
    [localPlayback, offlineRecord, offlinePlayability.playable],
  );
  const [materials, setMaterials] = useState<LessonMaterial[]>([]);
  const hasLessonMaterials = useMemo(() => materials.length > 0, [materials.length]);

  useEffect(() => {
    if (!hasLessonMaterials && activeTab === 'materials') {
      setActiveTab('overview');
    }
  }, [hasLessonMaterials, activeTab]);

  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isBookmarkIconHovered, setIsBookmarkIconHovered] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [lessonIdNumber, setLessonIdNumber] = useState<number | null>(null);
  const [courseIdNumber, setCourseIdNumber] = useState<number | null>(null);
  const [courseTitle, setCourseTitle] = useState<string | null>(() => {
    const hint = normalizeRouteQueryString(courseTitleParam);
    return hint.length > 0 ? hint : null;
  });

  const documentTitle = useMemo(
    () =>
      displayLessonTitle
        ? formatPageTitle(displayLessonTitle, courseTitle)
        : null,
    [displayLessonTitle, courseTitle],
  );
  useDocumentTitle(documentTitle);
  
  const [notationMode, setNotationMode] = useState<NotationMode>('video-notation');
  const [hasNotation, setHasNotation] = useState<boolean | null>(null);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [isFiftyPercentWatched, setIsFiftyPercentWatched] = useState(false);
  const [videoDuration, setVideoDuration] = useState(0);
  const scrollViewRef = useRef<ScrollView>(null);
  const currentTimeRef = useRef<number>(0);
  const isPlayingRef = useRef<boolean>(false);

  const pendingVideoIdRef = useRef<string | null>(null);
  const [pendingSeekSeconds, setPendingSeekSeconds] = useState<number | null>(null);

  const [suggestedLessons, setSuggestedLessons] = useState<Lesson[]>([]);
  const [mobNavPrevHover, setMobNavPrevHover] = useState(false);
  const [mobNavPrevPress, setMobNavPrevPress] = useState(false);
  const [mobNavNextHover, setMobNavNextHover] = useState(false);
  const [mobNavNextPress, setMobNavNextPress] = useState(false);
  const [completionSheetOpen, setCompletionSheetOpen] = useState(false);
  const [sidebarSheetOpen, setSidebarSheetOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const { expanded: sidebarExpanded, toggle: toggleSidebarExpanded } =
    useLessonSidebarExpanded(true);
  const [completionModalOpen, setCompletionModalOpen] = useState(false);
  const [upNextModalOpen, setUpNextModalOpen] = useState(false);
  const upNextModalOpenRef = useRef(false);
  useEffect(() => { upNextModalOpenRef.current = upNextModalOpen; }, [upNextModalOpen]);
  const videoEndedRef = useRef(false);
  const wasWatchedToEndRef = useRef(false);
  const hasSwitchedVideoRef = useRef(false);

  const [copyLinkFeedback, setCopyLinkFeedback] = useState<
    'idle' | 'copied' | 'shared' | 'error'
  >('idle');
  const copyFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareAtCurrentTime, setShareAtCurrentTime] = useState(true);
  const [shareAtLoop, setShareAtLoop] = useState(false);
  const [shareToolbarHovered, setShareToolbarHovered] = useState(false);
  const [shareToolbarPressed, setShareToolbarPressed] = useState(false);

  const clearCopyLinkFeedbackSoon = useCallback(() => {
    if (copyFeedbackTimerRef.current) clearTimeout(copyFeedbackTimerRef.current);
    copyFeedbackTimerRef.current = setTimeout(() => {
      setCopyLinkFeedback('idle');
      copyFeedbackTimerRef.current = null;
    }, COPY_LINK_TOAST_MS);
  }, []);

  useEffect(
    () => () => {
      if (copyFeedbackTimerRef.current) clearTimeout(copyFeedbackTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (shareModalOpen) {
      setShareAtCurrentTime(true);
      setShareAtLoop(false);
    }
  }, [shareModalOpen]);
  
  const handleNotationCheck = useCallback((notationExists: boolean) => {
    setHasNotation(notationExists);
    if (!notationExists) setNotationMode('video-only');
  }, []);

  const handleNotationVisibilityFromPlayer = useCallback((visible: boolean) => {
    setNotationMode(visible ? 'video-notation' : 'video-only');
  }, []);
  
  const isLandscapeMobile = useIsLandscapeMobile();
  const navigation = useNavigation();
  const isMobile = useIsMobile();
  const { height: screenHeight, width: screenWidth } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const queryClient = useQueryClient();

  const isPortraitViewport = screenHeight >= screenWidth;
  const portraitTopInsetRef = useRef(insets.top);
  useEffect(() => {
    if (isPortraitViewport && insets.top > 0) {
      portraitTopInsetRef.current = insets.top;
    }
  }, [isPortraitViewport, insets.top]);
  const focusBarTopInset = isPortraitViewport ? portraitTopInsetRef.current : 0;

  // User progress (continue watching, completed state, progress bars) is cached
  // with a long staleTime and has no per-save invalidation. Popping back here
  // (header / browser back) reuses the cached screen, so it showed stale data.
  // Refresh every user-progress surface when we leave a lesson, regardless of
  // how we navigated away (header back, browser back, breadcrumb, swipe).
  useEffect(() => {
    return () => {
      const progressKeys = [
        ['purchasedCourses', 'continue'], // courses hub "Continue"
        ['userLessons'], // byCourse (completed + left-off) + continueForDashboard
        ['userCourseProgress'], // course-card progress bars
        ['collection', 'userLessons'], // collection per-lesson completed state
        ['collection', 'continue'], // collection "Continue"
      ];
      progressKeys.forEach((queryKey) => {
        void queryClient.invalidateQueries({ queryKey });
      });
    };
  }, [queryClient]);

  const showShortcutsTab = Platform.OS === 'web' && !isMobile;

  useEffect(() => {
    if (!showShortcutsTab && activeTab === 'shortcuts') {
      setActiveTab('overview');
    }
  }, [showShortcutsTab, activeTab]);

  const isWebPlatform = Platform.OS === 'web';
  const showDesktopLessonChrome = isWebPlatform && !isMobile;
  // Focus mode is an in-app immersive player on the mobile layout (phone or
  // tablet). Landscape always takes over the screen (CEO ask: let the video +
  // notation use the full viewport); portrait is opt-in via the expand button.
  const focusActive = (focusMode || isLandscapeMobile) && !showDesktopLessonChrome;
  // The exit affordance only makes sense for opt-in portrait focus; in landscape
  // the user leaves by rotating back to portrait.
  const showFocusExit = focusActive && !isLandscapeMobile;
  // The content sits inside a centered max-width column with horizontal padding.
  // To bleed the focused player edge-to-edge we must cancel BOTH the centering
  // gutter (non-zero on wide tablets) and that padding — assuming a flat 16px
  // only worked on phones and left a gap on tablets.
  const focusBleedLeft =
    Math.max(0, (screenWidth - MOBILE_CONTENT_MAX_WIDTH) / 2) + MOBILE_CONTENT_PAD_X;
  const showCourseLessonsBottomInset =
    Platform.OS !== 'web' && isMobile && !isLandscapeMobile
      ? 16
      : insets.bottom + 16;

  const [sidebarSeasonId, setSidebarSeasonId] = useState<number | null>(null);
  const [simpleSeedSlug, setSimpleSeedSlug] = useState<string | null>(null);
  const seasonBySlugRef = useRef<Record<string, number>>({});

  const sidebarQueryKey = useMemo(() => {
    if (!routeCourseSlug || (sidebarSeasonId == null && !activeLessonSlug)) {
      return ['lessonPage', 'sidebar', 'idle'] as const;
    }
    const uid = user?.id ?? null;
    if (sidebarSeasonId != null) {
      return ['lessonPage', 'sidebar', routeCourseSlug, 'season', sidebarSeasonId, uid] as const;
    }
    if (simpleSeedSlug) {
      return ['lessonPage', 'sidebar', routeCourseSlug, 'simple', uid] as const;
    }
    return ['lessonPage', 'sidebar', routeCourseSlug, 'lesson', activeLessonSlug, uid] as const;
  }, [routeCourseSlug, sidebarSeasonId, simpleSeedSlug, activeLessonSlug, user?.id]);

  const lessonSidebarQuery = useQuery({
    queryKey: sidebarQueryKey,
    queryFn: () =>
      fetchLessonSidebar({
        courseSlug: routeCourseSlug,
        lessonSlug: (simpleSeedSlug ?? activeLessonSlug) as string,
        userId: user?.id ?? null,
      }),
    staleTime: CATALOG_STALE_TIME,
    enabled: Boolean(activeLessonSlug) && Boolean(routeCourseSlug) && isOnline,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    const data = lessonSidebarQuery.data;
    if (!data) return;

    const seasonId = data.current_season?.id ?? null;
    if (seasonId != null) {
      if (data.lessons?.length) {
        const map = seasonBySlugRef.current;
        for (const l of data.lessons) map[normalizeRouteQueryString(l.slug)] = seasonId;
      }
      // Ignore stale placeholder data from a different season than the active query.
      if (sidebarSeasonId != null && seasonId !== sidebarSeasonId) return;
      if (sidebarSeasonId !== seasonId) {
        queryClient.setQueryData(
          ['lessonPage', 'sidebar', routeCourseSlug, 'season', seasonId, user?.id ?? null],
          data,
        );
        setSidebarSeasonId(seasonId);
      }
    } else if (!simpleSeedSlug && activeLessonSlug) {
      setSimpleSeedSlug(activeLessonSlug);
    }
  }, [lessonSidebarQuery.data, routeCourseSlug, user?.id, sidebarSeasonId, simpleSeedSlug, activeLessonSlug]);

  useEffect(() => {
    const data = lessonSidebarQuery.data;
    if (!sidebarSeasonId || !data?.lessons?.length) return;
    // Wait for fresh data matching the requested season before correcting.
    if (data.current_season?.id !== sidebarSeasonId) return;

    const inCurrentSeason = data.lessons.some(
      (l) => normalizeRouteQueryString(l.slug) === activeLessonSlug,
    );
    if (!inCurrentSeason) {
      const corrected = seasonBySlugRef.current[activeLessonSlug] ?? null;
      if (corrected !== sidebarSeasonId) {
        setSidebarSeasonId(corrected);
      }
    }
  }, [activeLessonSlug, sidebarSeasonId, lessonSidebarQuery.data]);

  const sidebarSheetSnapPoints = useMemo(
    () => [isLandscapeMobile ? 80 : 85],
    [isLandscapeMobile],
  );

  useEffect(() => {
    if (lessonIdNumber == null) return;
    setLessonVideosByLessonId((prev) => ({ ...prev, [lessonIdNumber]: videos }));
    setUserVideosByLessonId((prev) => ({ ...prev, [lessonIdNumber]: userVideos }));
  }, [lessonIdNumber, videos, userVideos]);

  const sidebarLessonsOrdered = lessonSidebarQuery.data?.lessons ?? [];

  const { navSiblingPreviousLesson, navSiblingNextLesson } = useMemo(() => {
    const s = normalizeRouteQueryString(activeLessonSlug);
    const idx = sidebarLessonsOrdered.findIndex((l) => normalizeRouteQueryString(l.slug) === s);
    return {
      navSiblingPreviousLesson: idx > 0 ? (sidebarLessonsOrdered[idx - 1] ?? null) : null,
      navSiblingNextLesson:
        idx >= 0 && idx < sidebarLessonsOrdered.length - 1
          ? (sidebarLessonsOrdered[idx + 1] ?? null)
          : null,
    };
  }, [sidebarLessonsOrdered, activeLessonSlug]);

  const currentVideoIndexInLesson = useMemo(
    () => (!currentVideo || videos.length === 0 ? -1 : videos.findIndex((v) => v.id === currentVideo.id)),
    [currentVideo, videos],
  );

  const hasNextNavigation =
    (videos.length > 1 && currentVideoIndexInLesson >= 0 && currentVideoIndexInLesson < videos.length - 1) ||
    Boolean(navSiblingNextLesson?.slug);

  const hasPrevNavigation =
    (videos.length > 1 && currentVideoIndexInLesson > 0) ||
    Boolean(navSiblingPreviousLesson?.slug);

  const fetchAndCacheLessonVideos = useCallback(
    async (lessonId: number): Promise<LessonVideo[]> => {
      const data = await queryClient.fetchQuery({
        queryKey: ['lessonVideosWithProgress', lessonId, user?.id ?? null] as const,
        queryFn: () => fetchLessonVideosWithProgress({ lessonId, userId: user?.id ?? null }),
        staleTime: CATALOG_STALE_TIME,
      });
      const sorted = [...(data.videos ?? [])].sort((a, b) => (a.order_no ?? 0) - (b.order_no ?? 0));
      setLessonVideosByLessonId((prev) => ({ ...prev, [lessonId]: sorted }));
      setUserVideosByLessonId((prev) => ({ ...prev, [lessonId]: data.user_videos ?? [] }));
      return sorted;
    },
    [user?.id],
  );

  const handleSidebarSiblingExpand = useCallback(
    async (lessonId: number) => {
      if (lessonVideosByLessonId[lessonId]?.length) return;
      if (siblingLoadingLessonIds.includes(lessonId)) return;
      setSiblingLoadingLessonIds((ids) => [...ids, lessonId]);
      try {
        await fetchAndCacheLessonVideos(lessonId);
      } finally {
        setSiblingLoadingLessonIds((ids) => ids.filter((id) => id !== lessonId));
      }
    },
    [lessonVideosByLessonId, siblingLoadingLessonIds, fetchAndCacheLessonVideos],
  );

  const { handlePlayerStateChange, handleNativeTrackingEvent } = useSoundsliceTracker(
    isWebPlatform,
    !playerLoading && !localPlayback,
    lessonIdNumber,
    currentTime,
    videoDuration,
    user as User | null,
    routeCourseSlug,
    activeLessonSlug,
    membershipTier,
    currentVideo?.id,
    currentVideo?.slug,
  );
  
  const embedUrlResult = useMemo<{ url: string | null; error: UnknownApplicationError | null }>(() => {
    if (!currentVideo?.ss_url) {
      return { url: null, error: new UnknownApplicationError('Invalid or missing video URL provided.') };
    }
    try {
      const wpUserId = user?.user_metadata?.wp_user_id;
      const fallbackId = user?.id;
      const ssUserId: string | undefined = wpUserId ? String(wpUserId) : fallbackId;

      if (!wpUserId) {
        logger.info('Using Supabase user ID as fallback for Soundslice (WP user ID not available)', {
          supabaseUserId: fallbackId,
          lessonSlug: activeLessonSlug,
          courseSlug: routeCourseSlug,
        });
      }

      return { url: getEmbedUrl(currentVideo.ss_url, ssUserId), error: null };
    } catch (err) {
      logger.error('Failed to calculate embed URL:', {
        lessonId: lesson?.id,
        videoId: currentVideo?.id,
        ssUrl: currentVideo?.ss_url,
        wpUserId: user?.user_metadata?.wp_user_id,
        supabaseUserId: user?.id,
        error: err,
      });
      const errorMessage = 'Failed to generate video embed URL' + (err instanceof Error ? err.message : '');
      return {
        url: null,
        error: new UnknownApplicationError(
          errorMessage,
          err instanceof Error && 'code' in err ? String(err.code) : null,
        ),
      };
    }
  }, [currentVideo?.ss_url, currentVideo?.id, lesson?.id, user?.user_metadata?.wp_user_id, user?.id, activeLessonSlug, routeCourseSlug]);

  const embedUrl = embedUrlResult.url;

  useEffect(() => {
    if (embedUrlResult.error) setError(embedUrlResult.error);
  }, [embedUrlResult.error]);

  const currentVideoProgress = useMemo(() => {
    if (!currentVideo) return null;
    return getUserProgressForVideo(currentVideo.id, userVideos);
  }, [currentVideo, userVideos]);

  const isCurrentVideoCompleted = useMemo(() => {
    return currentVideoProgress?.is_completed || false;
  }, [currentVideoProgress]);

  const nextVideo = useMemo(() => {
    if (!currentVideo || videos.length === 0) return null;
    const idx = videos.findIndex(v => v.id === currentVideo.id);
    if (idx >= 0 && idx < videos.length - 1) return videos[idx + 1];
    return null;
  }, [currentVideo, videos]);

  const currentTimeWholeSeconds = useMemo(() => Math.floor(currentTime), [currentTime]);

  const lessonShareUrl = useMemo(() => {
    if (!routeCourseSlug || !activeLessonSlug) return '';
    const collectionIdFromQuery = parseNumericCollectionId(globalParams.collectionId);
    return buildAbsoluteLessonShareUrl(
      buildLessonShareUrlArgs({
        courseSlug: routeCourseSlug,
        lessonSlug: activeLessonSlug,
        collectionId: collectionIdFromQuery,
        referrer: typeof referrer === 'string' && referrer.length > 0 ? referrer : undefined,
        referrerTitle:
          typeof referrerTitle === 'string' && referrerTitle.length > 0 ? referrerTitle : undefined,
        currentTimeSeconds: currentTimeWholeSeconds,
        lessonHasMultipleVideos: videos.length > 1,
        currentVideo,
        shareAtCurrentTime,
        includeLoop: shareAtLoop,
      }),
    );
  }, [
    routeCourseSlug,
    activeLessonSlug,
    globalParams.collectionId,
    referrer,
    referrerTitle,
    currentTimeWholeSeconds,
    videos.length,
    currentVideo,
    shareAtCurrentTime,
    shareAtLoop,
  ]);

  const resumeFromSavedProgressSeconds = useMemo(() => {
    if (!currentVideoProgress || !currentVideo?.duration) return 0;

    const savedTime = currentVideoProgress.left_off_at_seconds ?? 0;

    if (savedTime > 0 && savedTime >= currentVideo.duration - 5) {
      return Math.floor(currentVideo.duration);
    }

    return savedTime;
  }, [currentVideoProgress, currentVideo?.duration]);

  const initialSeekFromDeeplink = useMemo(
    () =>
      deepLinkSeekSecondsForCurrentVideo({
        urlTParam: globalParams.t,
        urlVideoParam,
        currentVideo,
        lessonHasOnlyOneVideo: videos.length === 1,
      }),
    [globalParams.t, urlVideoParam, currentVideo?.id, currentVideo?.slug, currentVideo?.duration, videos.length],
  );

  const effectivePlayerStartSeconds =
    pendingSeekSeconds ?? initialSeekFromDeeplink ?? resumeFromSavedProgressSeconds;
  
  // Available height for the player when it takes over the screen in focus mode.
  // In landscape the screen's short side can be smaller than the portrait min
  // height, so the floor must be orientation-aware to avoid overflowing.
  // With the downloaded file in landscape there is nothing useful to show
  // above the native player (no notation toggle), so the bar is dropped and
  // the video takes the whole screen; rotating back to portrait restores
  // everything.
  const focusBarHidden = isLandscapeMobile && localPlayback;
  const focusPlayerHeight = Math.max(
    isLandscapeMobile ? 200 : MOBILE_PLAYER_MIN_HEIGHT,
    screenHeight - insets.top - insets.bottom - (focusBarHidden ? 0 : FOCUS_BAR_H),
  );

  const videoHeight = useMemo(() => {
    if (focusActive) {
      return focusPlayerHeight;
    }
    if (isLandscapeMobile) {
      return screenHeight * 0.75;
    }
    return null;
  }, [focusActive, focusPlayerHeight, isLandscapeMobile, screenHeight]);

  const playerShellStyle = useMemo(() => {
    if (focusActive) {
      return {
        height: focusPlayerHeight,
        overflow: 'hidden' as const,
        backgroundColor: '#000',
        borderRadius: 0,
      };
    }
    if (videoHeight != null) {
      return [
        {
          width: '100%' as const,
          maxWidth: '100%' as const,
          height: videoHeight,
          overflow: 'hidden' as const,
          backgroundColor: '#FFF',
          borderRadius: 8,
        },
      ];
    }
    if (showDesktopLessonChrome) {
      return {
        width: '100%' as const,
        maxWidth: '100%' as const,
        aspectRatio: 16 / 9,
        overflow: 'hidden' as const,
        backgroundColor: '#FFF',
        borderRadius: 8,
        alignSelf: 'center' as const,
      };
    }
    if (isMobile && !isLandscapeMobile) {
      const contentWidth = Math.max(0, screenWidth - 32);
      return {
        width: '100%' as const,
        maxWidth: '100%' as const,
        height: getMobilePlayerHeight(contentWidth, screenHeight),
        overflow: 'hidden' as const,
        backgroundColor: '#FFF',
        borderRadius: 8,
      };
    }
    return styles.playerCard;
  }, [focusActive, focusPlayerHeight, videoHeight, isMobile, isLandscapeMobile, screenWidth, screenHeight, showDesktopLessonChrome]);

  useFocusEffect(
    React.useCallback(() => {
      if (Platform.OS === 'web') {
        return () => {};
      }
      return () => {
        try {
          playerRef.current?.pause();
        } catch (e) {
          __DEV__ && logger.debug('[useFocusEffect] player pause failed:', e);
        }
        handlePlayerStateChange(false);
      };
    }, [handlePlayerStateChange])
  );

  React.useLayoutEffect(() => {
    if (isLandscapeMobile || focusActive) {
      navigation.setOptions({ headerShown: false, tabBarStyle: { display: 'none' } });
    } else {
      navigation.setOptions({
        headerShown: true,
        title: courseTitle || '',
      });
    }
  }, [isLandscapeMobile, focusActive, navigation, courseTitle]);

  // When entering focus mode the player must sit flush at the top of the screen.
  useEffect(() => {
    if (focusActive) {
      scrollViewRef.current?.scrollTo({ y: 0, animated: false });
    }
  }, [focusActive]);

  // Tell the tab navigator to hide the bottom tab bar during focus mode.
  useEffect(() => {
    setPlayerFocusActive(focusActive);
    return () => setPlayerFocusActive(false);
  }, [focusActive]);

  // Hide the OS status bar (clock/battery) for a true full-screen experience.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    setStatusBarHidden(focusActive, 'fade');
    return () => setStatusBarHidden(false, 'fade');
  }, [focusActive]);

  const enterFocusMode = useCallback(() => {
    if (Platform.OS !== 'web') {
      void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    }
    setFocusMode(true);
  }, []);

  const exitFocusMode = useCallback(() => {
    if (Platform.OS !== 'web') {
      void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light).catch(() => {});
    }
    setFocusMode(false);
  }, []);

  // Android hardware back should collapse opt-in (portrait) focus first, not
  // leave the lesson. Landscape focus is left untouched (rotating back is the
  // exit), so back keeps its normal navigation behavior there.
  useEffect(() => {
    if (Platform.OS !== 'android' || !focusMode || isLandscapeMobile) return;
    const sub = BackHandler.addEventListener('hardwareBackPress', () => {
      setFocusMode(false);
      return true;
    });
    return () => sub.remove();
  }, [focusMode, isLandscapeMobile]);

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    if (playerLoading || !embedUrl) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.isContentEditable ||
        target.closest('input, textarea, [contenteditable="true"]')
      ) return;

      const key = event.key.toLowerCase();

      if (event.code === 'Space' || key === 'k') {
        event.preventDefault();
        isPlayingRef.current = !isPlayingRef.current;
        if (isPlayingRef.current) {
          playerRef.current?.play();
        } else {
          playerRef.current?.pause();
        }
        return;
      }

      if (key === 'j') {
        event.preventDefault();
        const newTime = Math.max(0, currentTimeRef.current - 15);
        currentTimeRef.current = newTime;
        playerRef.current?.seekTo(newTime);
        setCurrentTime(newTime);
        return;
      }

      if (key === 'l') {
        event.preventDefault();
        const newTime = Math.min(videoDuration || Infinity, currentTimeRef.current + 15);
        currentTimeRef.current = newTime;
        playerRef.current?.seekTo(newTime);
        setCurrentTime(newTime);
        return;
      }

      if (key === 'f') {
        event.preventDefault();
        playerRef.current?.toggleFullscreen();
        return;
      }

      if (event.code === 'ArrowLeft') {
        event.preventDefault();
        const newTime = Math.max(0, currentTimeRef.current - 5);
        currentTimeRef.current = newTime;
        playerRef.current?.seekTo(newTime);
        setCurrentTime(newTime);
        return;
      }

      if (event.code === 'ArrowRight') {
        event.preventDefault();
        const newTime = Math.min(videoDuration || Infinity, currentTimeRef.current + 5);
        currentTimeRef.current = newTime;
        playerRef.current?.seekTo(newTime);
        setCurrentTime(newTime);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [videoDuration, playerLoading, embedUrl]);

  useEffect(() => {
    if (effectivePlayerStartSeconds > 0) {
      setCurrentTime(effectivePlayerStartSeconds);
      currentTimeRef.current = effectivePlayerStartSeconds;
      lastSavedTimeRef.current = effectivePlayerStartSeconds;
    }
  }, [effectivePlayerStartSeconds]);

  useEffect(() => {
    if (!currentVideo) return;

    if (hasSwitchedVideoRef.current === false && currentVideo) {
      hasSwitchedVideoRef.current = true;
    }

    videoEndedRef.current = false;
    setUpNextModalOpen(false);
    setIsFiftyPercentWatched(false);

    const videoProgress = getUserProgressForVideo(currentVideo.id, userVideos);
    const wasWatchedToEnd = videoProgress?.left_off_at_seconds
      && currentVideo.duration
      && videoProgress.left_off_at_seconds >= currentVideo.duration - 5;
    wasWatchedToEndRef.current = wasWatchedToEnd || false;

    setNotationMode('video-notation');
    setHasNotation(null);

    if (effectivePlayerStartSeconds === 0) return;

    const delay =
      Platform.OS === 'web' ? SEEK_AFTER_VIDEO_CHANGE_MS_WEB : SEEK_AFTER_VIDEO_CHANGE_MS_NATIVE;
    const timeout = setTimeout(() => {
      playerRef.current?.seekTo(effectivePlayerStartSeconds);
    }, delay);
    return () => clearTimeout(timeout);
  }, [currentVideo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (embedUrl) {
      setPlayerLoading(true);
      setHasNotation(null);
    }
  }, [embedUrl, lessonIdNumber]);

  // Downloaded file: captions come from the downloaded VTT; notation never exists.
  const wasLocalPlaybackRef = useRef(false);
  useEffect(() => {
    if (!localPlayback) {
      setOfflineCaptionCues([]);
      // Back on the Soundslice player: undo the video-only state the
      // downloaded player forced so notation is detected again.
      if (wasLocalPlaybackRef.current) {
        wasLocalPlaybackRef.current = false;
        setHasNotation(null);
        setNotationMode('video-notation');
        setPlayerLoading(true);
      }
      return;
    }
    wasLocalPlaybackRef.current = true;
    setHasNotation(false);
    setNotationMode('video-only');
    const text = offlineRecord?.captionsPath ? readOfflineText(offlineRecord.captionsPath) : null;
    setOfflineCaptionCues(text ? parseVtt(text) : []);
    if (!offlinePlayability.playable) setPlayerLoading(false);
  }, [localPlayback, offlineRecord?.captionsPath, offlinePlayability.playable, currentVideo?.id]);

  const currentVideoLessonId = currentVideo?.lesson_id ?? null;
  const choosePlayback = useCallback(
    (source: PlaybackSource) => {
      setVisitPlaybackChoice({ lessonId: currentVideoLessonId, source });
      setPlayerLoading(true);
    },
    [currentVideoLessonId],
  );

  // Switch link under the player: swap players and keep the position.
  const switchPlayback = useCallback(
    (source: PlaybackSource) => {
      const at = Math.floor(currentTimeRef.current);
      if (at > 0) setPendingSeekSeconds(at);
      // An explicit request also covers a download that finished after the
      // video was opened.
      setOpenedVideo((prev) => (prev.downloaded ? prev : { ...prev, downloaded: true }));
      choosePlayback(source);
    },
    [choosePlayback],
  );

  useEffect(() => {
    setVisitPlaybackChoice((prev) => (prev && prev.lessonId !== currentVideoLessonId ? null : prev));
  }, [currentVideoLessonId]);

  // Runs once per video that needs a decision; Cancel leaves the same two
  // options on the player area.
  useEffect(() => {
    if (playbackDecision !== 'choose' || currentVideo?.id == null) return;
    Alert.alert('This video is downloaded', 'How do you want to watch?', [
      { text: 'Stream online', onPress: () => choosePlayback('stream') },
      { text: 'Play download', onPress: () => choosePlayback('download') },
      { text: 'Cancel', style: 'cancel' },
    ]);
  }, [playbackDecision, currentVideo?.id, choosePlayback]);

  const offlinePlayTrackedRef = useRef<number | null>(null);
  const handleOfflinePlayingChange = useCallback(
    (playing: boolean) => {
      if (!playing || !currentVideo || !offlineRecord) return;
      if (offlinePlayTrackedRef.current === currentVideo.id) return;
      offlinePlayTrackedRef.current = currentVideo.id;
      void mixpanelService.trackOfflineVideoPlayed(offlineRecord, user, fullPath);
    },
    [currentVideo, offlineRecord, user, fullPath],
  );

  // Resume position: direct write online, queued for replay while offline.
  const persistLeftOff = useCallback(
    (videoId: number, seconds: number) => {
      if (!user?.id || !lessonIdNumber) return;
      if (offlinePlaybackRef.current) {
        void enqueueLeftOff(user.id, videoId, lessonIdNumber, seconds);
      } else {
        updateVideoLeftOffTime(user.id, videoId, seconds, lessonIdNumber);
      }
    },
    [user?.id, lessonIdNumber],
  );

  const loadLessonData = React.useCallback(async () => {
    if (!user?.id) return;
    const userId = user.id;

    // Shared hydration for the network payload and the offline snapshot.
    const applyLessonData = async (lessonData: LessonPageData, fromSnapshot: boolean) => {
      __DEV__ && logger.debug('[LessonScreen] lessonData:', { fromSnapshot, lessonData });

      // If the response's course differs from the URL's course, the lesson was
      // resolved via the redirect-course lesson_mapping fallback in the RPC
      // (e.g. a WP HG Classic deep link landing on the Josephine HGU lesson).
      // Replace with the canonical URL so breadcrumbs, share URLs, bookmarks,
      // and "up next" use the right context.
      // We intentionally check only the course slug — the lesson_videos slug
      // fallback (RPC section 2b) returns a lesson whose slug differs from the
      // URL but keeps the same course, and we must NOT redirect in that case.
      // Snapshots are stored under canonical slugs, so no redirect offline.
      const canonicalCourseSlug = lessonData.course?.slug;
      const canonicalLessonSlug = lessonData.lesson?.slug;
      if (
        !fromSnapshot &&
        canonicalCourseSlug &&
        canonicalLessonSlug &&
        canonicalCourseSlug !== String(courseSlug)
      ) {
        router.replace(`/courses/${canonicalCourseSlug}/${canonicalLessonSlug}` as any);
        return;
      }

      setOfflinePlayback(fromSnapshot);
      setPageData(lessonData);

      // Set lesson data
      setLesson(lessonData.lesson);
      setLessonSummary(lessonData.lesson.summary);
      
      const sortedVideos = [...(lessonData.videos || [])]
        .sort((a, b) => (a.order_no ?? 0) - (b.order_no ?? 0));
      
      setVideos(sortedVideos);
      // Offline: overlay progress saved locally since the snapshot was taken.
      setUserVideos(
        fromSnapshot
          ? applyQueuedProgress(lessonData.user_videos || [], await getQueuedProgress(userId))
          : lessonData.user_videos || [],
      );
      const rawMaterials = fromSnapshot ? [] : lessonData.materials ?? [];
      setMaterials(rawMaterials);
      // Files-domain audio is signature-gated and the players fetch the URL at
      // render time, so swap in signed URLs as soon as they are minted.
      if (!fromSnapshot) {
        void signAudioMaterialUrls(rawMaterials).then((signedMaterials) => {
          if (signedMaterials !== rawMaterials) setMaterials(signedMaterials);
        });
      }
      setCourseTitle(lessonData.course.title)
      
      const videoOverride = pendingVideoIdRef.current;
      pendingVideoIdRef.current = null;
      const initialVideo = pickInitialLessonVideo(sortedVideos, videoOverride ?? urlVideoParam);
      setCurrentVideo(initialVideo);
      
      setVideoDuration(initialVideo?.duration || 0);
      
      const lessonId = lessonData.lesson?.id;
      if (lessonId) {
        setLessonIdNumber(Number(lessonId));
      }
      
      const effectiveCourseId =
        lessonData.lesson?.course_id ?? lessonData.course?.id ?? null;
      if (effectiveCourseId) {
        setCourseIdNumber(Number(effectiveCourseId));
      }
      if(lessonData.user_lesson){
        setIsBookmarked(Boolean(lessonData.is_bookmarked));
        setIsCompleted(Boolean(lessonData.user_lesson.is_completed));
      } else {
        setIsBookmarked(Boolean(lessonData.is_bookmarked));

        if (lessonId && user?.id && lessonData.lesson?.status >= 3 && !fromSnapshot) {
          try {
            await markLessonOpened(user.id, Number(lessonId));
          } catch (error) {
            logger.error('Failed to mark lesson as opened:', {
              lessonId: lessonId,
              userId: user.id,
              error
            });
          }
        }
      }

      const wpCourseIdFromCourse = lessonData.course.wp_id ?? lessonData.lesson.wp_course_id ?? null;
      if (wpCourseIdFromCourse && !fromSnapshot) {
        const numericWpCourseId = Number(wpCourseIdFromCourse);
          wpAuth.notifyCourseActivity(numericWpCourseId);
      }
    };

    try {
      setDataLoading(true);
      setError(null);

      // Downloaded lessons carry a frozen copy of this payload. Offline it is
      // the only source; online it is the fallback when the network call
      // fails for any reason other than an access / not-found answer.
      const snapshot =
        Platform.OS !== 'web' ? loadLessonSnapshot(routeCourseSlug, activeLessonSlug) : null;
      const online = await getIsOnline();
      if (!online) {
        if (!snapshot) {
          throw new NetworkError("You're offline and this lesson isn't downloaded. Connect to the internet to watch it.");
        }
        await applyLessonData(snapshot.data, true);
        return;
      }

      let lessonData: LessonPageData;
      try {
        lessonData = await fetchLessonPageData(activeLessonSlug, routeCourseSlug, userId);
      } catch (fetchErr) {
        if (
          snapshot &&
          !(fetchErr instanceof PermissionDeniedError) &&
          !(fetchErr instanceof ResourceNotFoundError)
        ) {
          logger.warn('[LessonScreen] lesson fetch failed, using offline snapshot', {
            lessonSlug: activeLessonSlug,
            error: fetchErr,
          });
          await applyLessonData(snapshot.data, true);
          return;
        }
        throw fetchErr;
      }
      await applyLessonData(lessonData, false);

    } catch (err: any) {
      logger.error('Failed to load lesson data:', {
        lessonSlug: activeLessonSlug,
        courseSlug: routeCourseSlug,
        userId: user?.id,
        error: err,
        errorType: err?.constructor?.name
      });
      setError(err);
    } finally {
      setDataLoading(false);
    }
  }, [activeLessonSlug, routeCourseSlug, user?.id, urlVideoParam]);

  useEffect(() => {
    loadLessonData();
  }, [loadLessonData]);

  useEffect(() => {
    // Suggested lessons are fetched only when marking complete.
    setSuggestedLessons([]);
  }, [lesson?.id]);

  const navigateLessonInPlace = useCallback(
    (slug: string) => {
      const next = normalizeRouteQueryString(slug);
      if (!next || next === activeLessonSlug) return false;
      setActiveLessonSlug(next);
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.history.pushState(null, '', `/courses/${routeCourseSlug}/${next}`);
      }
      return true;
    },
    [activeLessonSlug, routeCourseSlug],
  );

  const handleSidebarSelectLesson = useCallback(
    (slug: string) => {
      navigateLessonInPlace(slug);
    },
    [navigateLessonInPlace],
  );

  const handleSidebarSelectVideo = useCallback(
    (video: LessonVideo) => {
      if (currentVideo?.id === video.id) return;
      setCurrentVideo(video);
      const p = getUserProgressForVideo(video.id, userVideos);
      const start = p?.left_off_at_seconds ?? 0;
      setCurrentTime(start);
      currentTimeRef.current = start;
      lastSavedTimeRef.current = start;
      setVideoDuration(video.duration || 0);
    },
    [currentVideo?.id, userVideos],
  );

  const handlePreviousLesson = useCallback(async () => {
    const idx = videos.findIndex((v) => v.id === currentVideo?.id);
    if (videos.length > 1 && idx > 0) {
      handleSidebarSelectVideo(videos[idx - 1]);
      return;
    }
    const prevL = navSiblingPreviousLesson;
    if (!prevL?.slug) return;
    let sorted = lessonVideosByLessonId[prevL.id];
    if (!sorted?.length) {
      sorted = await fetchAndCacheLessonVideos(prevL.id);
    } else {
      sorted = [...sorted].sort((a, b) => (a.order_no ?? 0) - (b.order_no ?? 0));
    }
    const last = sorted[sorted.length - 1];
    if (last) pendingVideoIdRef.current = String(last.id);
    navigateLessonInPlace(normalizeRouteQueryString(prevL.slug));
  }, [
    videos,
    currentVideo?.id,
    navSiblingPreviousLesson,
    lessonVideosByLessonId,
    fetchAndCacheLessonVideos,
    handleSidebarSelectVideo,
    navigateLessonInPlace,
  ]);

  const handleNextLesson = useCallback(() => {
    const idx = videos.findIndex((v) => v.id === currentVideo?.id);
    if (videos.length > 1 && idx >= 0 && idx < videos.length - 1) {
      handleSidebarSelectVideo(videos[idx + 1]);
      return;
    }
    const nextL = navSiblingNextLesson;
    if (!nextL?.slug) return;
    pendingVideoIdRef.current = null;
    navigateLessonInPlace(normalizeRouteQueryString(nextL.slug));
  }, [videos, currentVideo?.id, navSiblingNextLesson, handleSidebarSelectVideo, navigateLessonInPlace]);

  const handleSidebarChapterSeek = useCallback((seconds: number) => {
    const t = Math.max(0, Math.floor(seconds));
    playerRef.current?.seekTo(t);
    setCurrentTime(t);
    currentTimeRef.current = t;
  }, []);

  const handleSidebarChapterLinkClick = useCallback(
    (lessonSlug: string, video: LessonVideo, chapter: LessonVideoChapter) => {
      if (!routeCourseSlug) return;
      const normalizedLessonSlug = normalizeRouteQueryString(lessonSlug);
      if (!normalizedLessonSlug) return;
      const pathWithQuery = buildLessonUrl({
        courseSlug: routeCourseSlug,
        lessonSlug: normalizedLessonSlug,
        videoSlug: video.slug,
        startSeconds: chapter.begin ?? undefined,
      });
      mixpanelService.trackChapterLinkClick(
        routeCourseSlug,
        normalizedLessonSlug,
        video.slug ?? '',
        chapter.title?.trim() ?? '',
        Math.max(0, Math.floor(chapter.begin ?? 0)),
        user,
        pathWithQuery,
      );
    },
    [routeCourseSlug, user],
  );

  const handleSidebarSelectLessonAtVideo = useCallback(
    (lessonSlug: string, video: LessonVideo) => {
      const next = normalizeRouteQueryString(lessonSlug);
      if (!next) return;
      if (next === activeLessonSlug) {
        handleSidebarSelectVideo(video);
        return;
      }
      pendingVideoIdRef.current = String(video.id);
      navigateLessonInPlace(next);
    },
    [activeLessonSlug, handleSidebarSelectVideo, navigateLessonInPlace],
  );

  const handleSidebarSelectLessonAtChapter = useCallback(
    (lessonSlug: string, video: LessonVideo, chapter: LessonVideoChapter) => {
      const next = normalizeRouteQueryString(lessonSlug);
      if (!next) return;
      const seekTo = chapter.begin ?? 0;
      if (next === activeLessonSlug) {
        setPendingSeekSeconds(seekTo);
        handleSidebarSelectVideo(video);
        return;
      }
      pendingVideoIdRef.current = String(video.id);
      setPendingSeekSeconds(seekTo);
      navigateLessonInPlace(next);
    },
    [activeLessonSlug, handleSidebarSelectVideo, navigateLessonInPlace],
  );

  const handleSeasonPrev = useCallback(() => {
    const data = lessonSidebarQuery.data;
    if (!data?.current_season) return;
    const idx = data.seasons.findIndex((s) => s.id === data.current_season?.id);
    if (idx <= 0) return;
    const prev = data.seasons[idx - 1];
    if (!prev?.first_lesson_slug) return;
    setSidebarSeasonId(prev.id);
    navigateLessonInPlace(prev.first_lesson_slug);
  }, [lessonSidebarQuery.data, navigateLessonInPlace]);

  const handleSeasonNext = useCallback(() => {
    const data = lessonSidebarQuery.data;
    if (!data?.current_season) return;
    const idx = data.seasons.findIndex((s) => s.id === data.current_season?.id);
    if (idx < 0 || idx >= data.seasons.length - 1) return;
    const next = data.seasons[idx + 1];
    if (!next?.first_lesson_slug) return;
    setSidebarSeasonId(next.id);
    navigateLessonInPlace(next.first_lesson_slug);
  }, [lessonSidebarQuery.data, navigateLessonInPlace]);

  const sidebarHeaderTitle = useMemo(
    () => lessonSidebarQuery.data?.current_season?.title ?? lessonSidebarQuery.data?.course?.title ?? null,
    [lessonSidebarQuery.data?.current_season?.title, lessonSidebarQuery.data?.course?.title],
  );

  const sidebarSeasonTitle = useMemo(
    () => lessonSidebarQuery.data?.current_season?.title ?? null,
    [lessonSidebarQuery.data?.current_season?.title],
  );

  const { canPrevSeason, canNextSeason } = useMemo(() => {
    const data = lessonSidebarQuery.data;
    if (!data?.current_season) return { canPrevSeason: false, canNextSeason: false };
    const idx = data.seasons.findIndex((s) => s.id === data.current_season?.id);
    return {
      canPrevSeason: idx > 0,
      canNextSeason: idx >= 0 && idx < data.seasons.length - 1,
    };
  }, [lessonSidebarQuery.data]);

  const handleMobileSelectLessonAtVideo = useCallback(
    (slug: string, v: LessonVideo) => {
      handleSidebarSelectLessonAtVideo(slug, v);
      setSidebarSheetOpen(false);
    },
    [handleSidebarSelectLessonAtVideo],
  );

  const handleMobileSelectLessonAtChapter = useCallback(
    (slug: string, v: LessonVideo, ch: LessonVideoChapter) => {
      handleSidebarSelectLessonAtChapter(slug, v, ch);
      setSidebarSheetOpen(false);
    },
    [handleSidebarSelectLessonAtChapter],
  );

  const handleMobileSelectLesson = useCallback(
    (slug: string) => {
      handleSidebarSelectLesson(slug);
      setSidebarSheetOpen(false);
    },
    [handleSidebarSelectLesson],
  );

  // Fire once per course+lesson pair, after lesson data settles so the event
  // carries Course/Lesson Title (slug-only if the load failed). The key guard
  // keeps user/auth changes from double-counting the same visit.
  const lessonVisitTrackedKey = React.useRef<string | null>(null);
  useEffect(() => {
    if (!routeCourseSlug || !activeLessonSlug) return;
    if (dataLoading) return;
    const key = `${routeCourseSlug}|${activeLessonSlug}`;
    if (lessonVisitTrackedKey.current === key) return;
    lessonVisitTrackedKey.current = key;
    mixpanelService.trackLessonDetailVisit(
      routeCourseSlug,
      activeLessonSlug,
      user,
      fullPath,
      courseTitle,
      displayLessonTitle,
    );
  }, [routeCourseSlug, activeLessonSlug, dataLoading, courseTitle, displayLessonTitle, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!routeCourseSlug || !activeLessonSlug || !displayLessonTitle || Platform.OS !== 'web') return;
    gaService.trackLessonView(
      routeCourseSlug,
      activeLessonSlug,
      courseTitle || undefined,
      displayLessonTitle,
      user,
    );
  }, [routeCourseSlug, activeLessonSlug, displayLessonTitle, courseTitle, user?.id]);

  const lessonBeginTracked = React.useRef<string | null>(null);
  useEffect(() => {
    if (!routeCourseSlug || !activeLessonSlug || !lesson || !lessonIdNumber || Platform.OS !== 'web') {
      return;
    }
    if (lessonBeginTracked.current === activeLessonSlug) return;
    gaService.trackLessonBegin(
      routeCourseSlug,
      activeLessonSlug,
      courseTitle || undefined,
      displayLessonTitle || undefined,
      user
    );
    lessonBeginTracked.current = activeLessonSlug;
  }, [routeCourseSlug, activeLessonSlug, lesson, lessonIdNumber, courseTitle, displayLessonTitle, user?.id]);

  const {
    downloadingPdfId,
    downloadingAudioId,
    viewingPdf,
    closePdfViewer,
    handlePdfView,
    handleMaterialPress,
    handlePdfDownload,
    handleAudioDownload,
  } = useMaterialDownload({
    courseSlug: routeCourseSlug,
    lessonSlug: activeLessonSlug,
    user,
    fullPath,
  });

  const handleWebTimeUpdate = useCallback((time: number) => {
    // The raw playhead arrives ~4x/sec. Every UI consumer reads whole seconds
    // (currentTimeWholeSeconds) and the tracker only uses this as a ref-backed
    // fallback, so we keep the ref hot but only re-render on a second boundary.
    currentTimeRef.current = time;
    setCurrentTime((prev) => (Math.floor(prev) === Math.floor(time) ? prev : time));

    if (videoDuration > 0) {
      setIsFiftyPercentWatched(
        (prev) => prev || time >= videoDuration * 0.5,
      );

      if (time < videoDuration - 2) {
        videoEndedRef.current = false;
        wasWatchedToEndRef.current = false;
      }

      const isVideoEnded = time >= videoDuration - 1;
      if (isVideoEnded && !videoEndedRef.current) {
        videoEndedRef.current = true;

        if (wasWatchedToEndRef.current) return;

        const currentVideoIndex = videos.findIndex(v => v.id === currentVideo?.id);
        const isFirstVideo = currentVideoIndex === 0;
        const hasNextVideo = currentVideoIndex >= 0 && currentVideoIndex < videos.length - 1;

        if (isFirstVideo && hasNextVideo) setUpNextModalOpen(true);
      }
    }

    if (user?.id && currentVideo?.id && lessonIdNumber && time > 0 && !upNextModalOpenRef.current && Math.abs(time - lastSavedTimeRef.current) > 5) {
      let timeToSave = Math.floor(time);
      if (videoDuration > 0 && time >= videoDuration - 1) timeToSave = Math.floor(videoDuration);

      setUserVideos((prev) => {
        const idx = prev.findIndex(uv => uv.lesson_video_id === currentVideo.id);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], left_off_at_seconds: timeToSave };
          return next;
        }
        return [...prev, {
          id: '',
          user_id: user.id,
          lesson_video_id: currentVideo.id,
          lesson_id: lessonIdNumber,
          is_opened: true,
          is_completed: false,
          left_off_at_seconds: timeToSave,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        }];
      });

      persistLeftOff(currentVideo.id, timeToSave);
      lastSavedTimeRef.current = timeToSave;
    }
  }, [videoDuration, videos, currentVideo, user, lessonIdNumber, persistLeftOff]);

  const handleSaveToFolder = useCallback((isNowBookmarked: boolean) => {
    setIsBookmarked(isNowBookmarked);
  }, []);

  const handleShareModalCopied = useCallback(() => {
    setCopyLinkFeedback('copied');
    clearCopyLinkFeedbackSoon();
  }, [clearCopyLinkFeedbackSoon]);

  const handleShareModalCopyError = useCallback(() => {
    setCopyLinkFeedback('error');
    clearCopyLinkFeedbackSoon();
  }, [clearCopyLinkFeedbackSoon]);

  const handleUpNextPlay = useCallback(() => {
    if (!nextVideo) return;
    setUpNextModalOpen(false);
    setCurrentVideo(nextVideo);
    const progress = getUserProgressForVideo(nextVideo.id, userVideos);
    const start = progress?.left_off_at_seconds ?? 0;
    setCurrentTime(start);
    currentTimeRef.current = start;
    lastSavedTimeRef.current = start;
    setVideoDuration(nextVideo.duration || 0);
    setTimeout(() => { playerRef.current?.play(); }, 2500);
  }, [nextVideo, userVideos]);

  const handleUpNextCancel = useCallback(() => setUpNextModalOpen(false), []);

  const handleNotationChange = useCallback((newMode: NotationMode) => {
    setNotationMode(newMode);
    playerRef.current?.setPlayerView(newMode);
  }, []);

  const handleCompletionBack = useCallback(() => {
    try {
      setCompletionModalOpen(false);
      setCompletionSheetOpen(false);
      if (courseSlug) {
        router.replace({ pathname: '/courses/[course-slug]', params: { 'course-slug': String(courseSlug) } });
        return;
      }
    } catch (err) {
      logger.error('Failed to navigate back from completion modal:', { courseSlug, error: err });
    }
  }, [courseSlug, router]);

  const handleSuggestionPress = useCallback((lesson: Lesson) => {
    if (lesson.slug) {
      setCompletionModalOpen(false);
      setCompletionSheetOpen(false);
      router.replace(`/courses/${courseSlug}/${lesson.slug}`);
      return;
    }
    logger.warn('Missing course or lesson slug in suggestion:', lesson);
  }, [courseSlug, router]);

  const markBusyRef = useRef(false);
  const handleMarkComplete = useCallback(async () => {
    if (!user?.id || !lessonIdNumber || !currentVideo?.id) return;
    if (markBusyRef.current) return;
    markBusyRef.current = true;

    const existingVideo = userVideos.find(uv => uv.lesson_video_id === currentVideo.id);
    const isCurrentVideoAlreadyCompleted = existingVideo?.is_completed || false;

    setCompletionLoading(true);
    const originalUserVideos = userVideos;

    try {
      const newVideoCompletedState = !isCurrentVideoAlreadyCompleted;

      const updatedUserVideos = existingVideo
        ? userVideos.map(uv =>
            uv.lesson_video_id === currentVideo.id
              ? { ...uv, is_completed: newVideoCompletedState }
              : uv
          )
        : [...userVideos, {
            id: '',
            user_id: user.id,
            lesson_video_id: currentVideo.id,
            lesson_id: lessonIdNumber,
            is_opened: true,
            is_completed: newVideoCompletedState,
            left_off_at_seconds: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          }];

      setUserVideos(updatedUserVideos);
      if (offlinePlayback) {
        await enqueueCompleted(user.id, currentVideo.id, lessonIdNumber, newVideoCompletedState);
      } else {
        await markVideoCompleted(user.id, currentVideo.id, lessonIdNumber, newVideoCompletedState);
      }

      const allVideosCompleted = areAllVideosCompleted(videos, updatedUserVideos);
      if (allVideosCompleted && !isCompleted) {
        setIsCompleted(true);
        try {
          const navigation = offlinePlayback
            ? { suggestedLessons: [] }
            : await getLessonNavigation(lessonIdNumber, user.id);
          setSuggestedLessons(navigation.suggestedLessons ?? []);
        } catch (error) {
          logger.error('Failed to load suggested lessons:', {
            lessonId: lessonIdNumber,
            userId: user.id,
            error,
          });
          setSuggestedLessons([]);
        }
        if (isMobile) {
          setCompletionSheetOpen(true);
          setCompletionModalOpen(false);
        } else {
          setCompletionSheetOpen(false);
          setCompletionModalOpen(true);
        }
      } else if (!allVideosCompleted && isCompleted) {
        setIsCompleted(false);
      }
    } catch (err: any) {
      setUserVideos(originalUserVideos);
      logger.error('Failed to mark video/lesson as complete:', {
        lessonId: lessonIdNumber,
        videoId: currentVideo.id,
        userId: user.id,
        error: err,
      });
      showError(err?.message || 'Failed to mark as complete');
    } finally {
      setCompletionLoading(false);
      markBusyRef.current = false;
    }
  }, [user, lessonIdNumber, currentVideo, userVideos, videos, isCompleted, isMobile, showError, offlinePlayback]);

  const handleBack = useCallback(() => {
    handlePlayerStateChange(false);
    if (user?.id && currentVideo?.id && lessonIdNumber) {
      const finalTime = Math.floor(currentTime || lastSavedTimeRef.current || 0);
      if (finalTime > 0) persistLeftOff(currentVideo.id, finalTime);
    }
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined' && window.history.length > 1) return window.history.back();
      if (router.canGoBack?.()) return router.back();
      if (referrer) return router.replace(decodeURIComponent(String(referrer)) as any);
      if (courseSlug) return router.replace({ pathname: '/courses/[course-slug]', params: { 'course-slug': String(courseSlug) } });
      return router.replace('/courses');
    } else {
      if (router.canGoBack?.()) return router.back();
      if (referrer) return router.replace(decodeURIComponent(String(referrer)) as any);
      if (courseSlug) return router.replace({ pathname: '/courses/[course-slug]', params: { 'course-slug': String(courseSlug) } });
      return router.replace('/courses');
    }
  }, [handlePlayerStateChange, user, currentVideo, lessonIdNumber, currentTime, router, referrer, courseSlug, persistLeftOff]);

  const handleShouldStartLoad = useCallback((request: any) => {
    const url = request.url || request.nativeEvent.url;
    const isPDFDownload = (
      url.toLowerCase().endsWith('.pdf') ||
      url.includes('soundslice-print.s3.amazonaws.com')
    );
    if (isPDFDownload) {
      logger.info('Soundslice PDF detected. Opening externally.');
      Linking.openURL(url).catch(err => logger.error('Failed to open PDF link externally:', err));
      return false;
    }
    return true;
  }, []);

  // Stable player callbacks so the memoized LessonPlayer never re-renders on the
  // parent's once-per-second time updates.
  const handlePlayerError = useCallback((msg: string | null) => {
    setError(msg ? new Error(msg) : null);
  }, []);

  const handlePlayerReady = useCallback(() => {
    setPlayerLoading(false);
    setTimeout(() => setPendingSeekSeconds(null), 1200);
  }, []);

  if (dataLoading && !lesson) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center" bg="$bg">
        <CoursePagePremiumLoader variant="lesson" />
      </YStack>
    );
  }

  if (error) {
    if (error instanceof PermissionDeniedError) {
      const paidMember = isMemberTier(membershipTier) || appleMember;
      return (
        <YStack f={1} bg="#F4F1ED">
          <AccessDenied
            isMember={paidMember}
            onRetry={
              paidMember
                ? async () => {
                    await refreshMembership();
                    await loadLessonData();
                  }
                : undefined
            }
            onUpgrade={() => {
              // iOS must sell via Apple IAP (App Store 3.1.1) — route to the
              // in-app paywall. Web keeps the existing WordPress /join/ flow.
              if (Platform.OS !== 'web') {
                router.push(paidMember ? '/membership' : '/upgrade');
                return;
              }
              if (typeof window === 'undefined') return;
              if (paidMember) {
                router.push('/membership');
                return;
              }
              const returnPath = window.location.pathname + window.location.search;
              window.location.href = `${URLS.JOIN}?josephine_redirect=${encodeURIComponent(returnPath)}`;
            }}
          />
        </YStack>
      );
    }

    if (error instanceof ResourceNotFoundError) {
      return (
        <YStack f={1} bg="$bg">
          <ErrorDisplay
            error={error}
            onRetry={loadLessonData}
            onDismiss={() => {
              if (router.canGoBack?.()) {
                router.back();
              } else if (courseSlug) {
                router.replace({ pathname: '/courses/[course-slug]', params: { 'course-slug': String(courseSlug) } });
              } else {
                router.replace('/courses');
              }
            }}
          />
        </YStack>
      );
    }

    return (
      <YStack f={1} bg="$bg">
        <ErrorDisplay
          error={error}
          onRetry={loadLessonData}
          onDismiss={() => {
            if (router.canGoBack?.()) {
              router.back();
            } else if (courseSlug) {
              router.replace({ pathname: '/courses/[course-slug]', params: { 'course-slug': String(courseSlug) } });
            } else {
              router.replace('/courses');
            }
          }}
        />
      </YStack>
    );
  }

  if (!embedUrl && !offlinePlayback) {
    return (
      <YStack f={1} bg="$bg">
        <ErrorDisplay 
          error={new UnknownApplicationError("Invalid or missing video URL provided.")}
          onRetry={handleBack}
          retryText="Go Back"
        />
      </YStack>
    );
  }

  const mobPrevEnabled = hasPrevNavigation;
  const mobNextEnabled = hasNextNavigation;
  const mobPrevElevated = mobPrevEnabled && (mobNavPrevHover || mobNavPrevPress);
  const mobNextElevated = mobNextEnabled && (mobNavNextHover || mobNavNextPress);

  const lessonMainColumn = (
            <YStack
              w="100%"
              py={focusActive ? 0 : '$2.5'}
              maxWidth={showDesktopLessonChrome ? LESSON_DESKTOP_CONTENT_MAX_WIDTH : 1140}
              minWidth={0}
              mx={showDesktopLessonChrome ? 'auto' : undefined}
              als={showDesktopLessonChrome ? 'center' : 'center'}
            >
              {focusActive && !focusBarHidden ? (
                <XStack
                  key="focus-top-bar"
                  w={screenWidth}
                  ml={-focusBleedLeft}
                  mr={0}
                  ai="center"
                  gap="$2"
                  pl={12 + insets.left}
                  pr={12 + insets.right}
                  pt={focusBarTopInset + 6}
                  pb={6}
                  bg="#FFFFFF"
                  borderBottomWidth={1}
                  borderBottomColor="#E0DED8"
                >
                  {showFocusExit ? (
                    <Pressable
                      onPress={exitFocusMode}
                      hitSlop={6}
                      accessibilityRole="button"
                      accessibilityLabel="Exit full screen"
                      style={lessonNavCompactSurface(true, false)}
                    >
                      <Ionicons
                        name="contract"
                        size={20}
                        color={lessonNavForeground(true, false)}
                      />
                    </Pressable>
                  ) : null}
                  <XStack flex={1} jc="center">
                    <MarkCompleteButton
                      isCompleted={isCurrentVideoCompleted}
                      isLoading={completionLoading}
                      isFiftyPercentWatched={isFiftyPercentWatched}
                      onPress={handleMarkComplete}
                      isMobile
                      isActive
                      variant="toolbar"
                    />
                  </XStack>
                  {localPlayback ? (
                    offlineRecord?.hasNotation ? (
                      <Text style={styles.offlineNotationHint}>Notation available online</Text>
                    ) : null
                  ) : (
                    <VideoActionButtons
                      notationMode={notationMode}
                      hasNotation={hasNotation}
                      onNotationChange={handleNotationChange}
                      toolbarCompact
                    />
                  )}
                </XStack>
              ) : null}
              <YStack
                key="lesson-player-shell"
                w={focusActive ? screenWidth : '100%'}
                maxWidth={focusActive ? screenWidth : '100%'}
                ml={focusActive ? -focusBleedLeft : 0}
                mr={0}
                alignSelf="stretch"
                overflow="hidden"
                style={playerShellStyle}
                position="relative"
              >
                {playerLoading && playbackDecision !== 'choose' && (
                  <View style={styles.loadingOverlay}>
                    <ActivityIndicator size="large" color="#fff" />
                    <Text style={styles.loadingText}>Loading player...</Text>
                  </View>
                )}
                {localPlayback ? (
                  offlineVideoUri ? (
                    <LessonPlayerOffline
                      ref={playerRef}
                      fileUri={offlineVideoUri}
                      title={currentVideo?.title || displayLessonTitle || undefined}
                      subtitle={courseTitle || undefined}
                      artworkUri={offlineRecord?.artworkPath ? offlineFileUri(offlineRecord.artworkPath) : null}
                      captionCues={offlineCaptionCues}
                      startSeconds={effectivePlayerStartSeconds}
                      videoHeight={videoHeight}
                      onReady={handlePlayerReady}
                      onTimeUpdate={handleWebTimeUpdate}
                      onPlayingChange={handleOfflinePlayingChange}
                    />
                  ) : (
                    <View style={styles.offlineUnavailable}>
                      <Ionicons name="cloud-offline-outline" size={28} color="#FFFFFF" />
                      <Text style={styles.offlineUnavailableText}>
                        {playabilityMessage(offlinePlayability) ??
                          "This video isn't downloaded. Connect to the internet to watch it."}
                      </Text>
                    </View>
                  )
                ) : playbackDecision === 'choose' ? (
                  <View style={styles.offlineUnavailable}>
                    <Ionicons name="download-outline" size={28} color="#FFFFFF" />
                    <Text style={styles.offlineUnavailableText}>
                      This video is downloaded. How do you want to watch?
                    </Text>
                    <View style={styles.playbackChoiceRow}>
                      <Pressable
                        onPress={() => choosePlayback('stream')}
                        accessibilityRole="button"
                        style={styles.playbackChoiceButton}
                      >
                        <Text style={styles.playbackChoiceButtonText}>Stream online</Text>
                      </Pressable>
                      <Pressable
                        onPress={() => choosePlayback('download')}
                        accessibilityRole="button"
                        style={styles.playbackChoiceButton}
                      >
                        <Text style={styles.playbackChoiceButtonText}>Play download</Text>
                      </Pressable>
                    </View>
                  </View>
                ) : playbackDecision === 'pending' ? null : embedUrl ? (
                  <LessonPlayer
                    ref={playerRef}
                    lessonId={lessonIdNumber}
                    embedUrl={embedUrl}
                    startSeconds={effectivePlayerStartSeconds}
                    notationMode={notationMode}
                    videoHeight={videoHeight}
                    error={(error as Error | null)?.message || null}
                    setError={handlePlayerError}
                    onReady={handlePlayerReady}
                    onTimeUpdate={handleWebTimeUpdate}
                    onNotationCheck={handleNotationCheck}
                    onNotationVisibilityChange={handleNotationVisibilityFromPlayer}
                    onNativeTrackingEvent={handleNativeTrackingEvent}
                    onShouldStartLoad={handleShouldStartLoad}
                  />
                ) : null}

                <UpNextOverlay
                  open={upNextModalOpen}
                  onClose={handleUpNextCancel}
                  onPlay={handleUpNextPlay}
                  nextVideoTitle={nextVideo?.title || ''}
                  autoPlayDelay={15}
                />

                {!showDesktopLessonChrome && !focusActive && !playerLoading ? (
                  <Pressable
                    onPress={enterFocusMode}
                    hitSlop={10}
                    accessibilityRole="button"
                    accessibilityLabel="Enter full screen"
                    style={({ pressed }) => [
                      styles.fullscreenButton,
                      pressed && styles.fullscreenButtonPressed,
                    ]}
                  >
                    <Ionicons name="expand" size={18} color="#FFFFFF" />
                  </Pressable>
                ) : null}
              </YStack>

            <YStack
              key="lesson-detail-block"
              w="100%"
              pt={!isMobile ? 32 : 16}
              px={0}
              alignSelf="stretch"
              display={focusActive ? 'none' : 'flex'}
              $gtXs={showDesktopLessonChrome ? undefined : { paddingBottom: 100 }}
            >
              <XStack pb={isMobile ? 8 : 12} jc="space-between" ai="center" w="100%">
                <ContentTitle
                  flex={1}
                  pr="$3"
                  color="#0D0D0D"
                  // Desktop keeps the original size/weight; app + mobile web are
                  // 30% smaller and semibold so the title still reads as a title.
                  {...(showDesktopLessonChrome
                    ? { fontWeight: '500' as const }
                    : { fontWeight: '700' as const, fontSize: 14, lineHeight: 20, $gtMd: { fontSize: 17, lineHeight: 24 } })}
                >
                  {videos.length > 1 ? (currentVideo?.title ?? displayLessonTitle) : displayLessonTitle}
                </ContentTitle>
                {lessonIdNumber && courseIdNumber && (
                  <XStack ai="center" gap="$2">
                    <DownloadButton pageData={pageData} video={currentVideo} userId={user?.id} />
                    <Tooltip text="Share video link" placement="top">
                      <Pressable
                        onPress={() => setShareModalOpen(true)}
                        accessibilityLabel="Share video link"
                        accessibilityRole="button"
                        onHoverIn={() =>
                          Platform.OS === 'web' && setShareToolbarHovered(true)
                        }
                        onHoverOut={() => setShareToolbarHovered(false)}
                        onPressIn={() => setShareToolbarPressed(true)}
                        onPressOut={() => setShareToolbarPressed(false)}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 40,
                          minHeight: 40,
                          maxHeight: 40,
                          // Desktop keeps the labelled pill; app + mobile web
                          // use an icon-only circle like bookmark / download.
                          ...(showDesktopLessonChrome
                            ? { paddingVertical: 4, paddingHorizontal: 16, gap: 6, borderRadius: 30 }
                            : { width: 40, minWidth: 40, maxWidth: 40, borderRadius: 20 }),
                          borderWidth: 1,
                          borderColor:
                            shareToolbarHovered || shareToolbarPressed
                              ? '#0D0D0D'
                              : '#C9C5C3',
                          backgroundColor:
                            shareToolbarHovered || shareToolbarPressed
                              ? '#0D0D0D'
                              : '#FFFFFF',
                          ...(Platform.OS === 'web'
                            ? ({ cursor: 'pointer' } as const)
                            : {}),
                        }}
                      >
                        {showDesktopLessonChrome ? (
                          <SizableText
                            fontSize={16}
                            fontWeight="600"
                            lineHeight={24}
                            letterSpacing={0}
                            color={
                              shareToolbarHovered || shareToolbarPressed
                                ? '#FFFFFF'
                                : '#000000'
                            }
                          >
                            Share
                          </SizableText>
                        ) : null}
                        <MarkCompleteToolbarIcon
                          color={
                            shareToolbarHovered || shareToolbarPressed
                              ? '#FFFFFF'
                              : '#0D0D0D'
                          }
                        />
                      </Pressable>
                    </Tooltip>
                    <BookmarkSave
                      mode="lesson"
                      onSave={handleSaveToFolder}
                      showError={showError}
                      showSuccess={showSuccess}
                      lessonId={lessonIdNumber}
                      lessonUrl={buildLessonUrl({
                        courseSlug: routeCourseSlug,
                        lessonSlug: activeLessonSlug,
                        referrer: referrer,
                      })}
                      courseId={courseIdNumber}
                      placement="left"
                    >
                      <Button
                        circular
                        width={40}
                        height={40}
                        minWidth={40}
                        maxWidth={40}
                        minHeight={40}
                        maxHeight={40}
                        padding={0}
                        size="$3"
                        onMouseEnter={Platform.OS === 'web' ? () => setIsBookmarkIconHovered(true) : undefined}
                        onMouseLeave={Platform.OS === 'web' ? () => setIsBookmarkIconHovered(false) : undefined}
                      >
                        {isBookmarked ? (
                          <BookmarkPlusPressedIcon
                            width={DASHBOARD_TOOLBAR_ARTWORK_PX}
                            height={DASHBOARD_TOOLBAR_ARTWORK_PX}
                          />
                        ) : isBookmarkIconHovered ? (
                          <BookmarkPlusHoverIcon
                            width={DASHBOARD_TOOLBAR_ARTWORK_PX}
                            height={DASHBOARD_TOOLBAR_ARTWORK_PX}
                          />
                        ) : (
                          <BookmarkPlusDefaultIcon
                            width={DASHBOARD_TOOLBAR_ARTWORK_PX}
                            height={DASHBOARD_TOOLBAR_ARTWORK_PX}
                          />
                        )}
                      </Button>
                    </BookmarkSave>
                  </XStack>
                )}
              </XStack>

              {hasPlayableDownload && !offlinePlayback && (playbackDecision === 'download' || playbackDecision === 'stream') ? (
                <XStack ai="center" gap={6} pb={isMobile ? 8 : 12}>
                  <Text style={styles.playbackSwitchLabel}>
                    {localPlayback ? 'Playing your download' : 'Streaming online'} ·
                  </Text>
                  <Pressable
                    onPress={() => switchPlayback(localPlayback ? 'stream' : 'download')}
                    accessibilityRole="button"
                    hitSlop={8}
                  >
                    <Text style={styles.playbackSwitchLink}>
                      {localPlayback ? 'Stream online instead' : 'Play download instead'}
                    </Text>
                  </Pressable>
                </XStack>
              ) : null}

              <XStack
                ai="flex-end"
                gap={isMobile ? "$1" : showDesktopLessonChrome ? "$1" : "$2"}
                px={isMobile ? 0 : 8}
                style={[styles.tabsContainer, isMobile && styles.tabsContainerMobile]}
              >
                <TouchableOpacity
                  onPress={() => setActiveTab('overview')}
                  style={[
                    styles.tabButton,
                    !isMobile && showDesktopLessonChrome && styles.tabButtonSidebar,
                    !isMobile && !showDesktopLessonChrome && styles.tabButtonMid,
                    isMobile && styles.tabButtonMobile,
                  ]}
                >
                  <SubtitleText color="#0D0D0D">Overview</SubtitleText>
                  <View style={[styles.tabUnderline, { opacity: activeTab === 'overview' ? 1 : 0 }]} />
                </TouchableOpacity>
                {hasLessonMaterials && (
                  <TouchableOpacity
                    onPress={() => setActiveTab('materials')}
                    style={[
                      styles.tabButton,
                      !isMobile && showDesktopLessonChrome && styles.tabButtonSidebar,
                      !isMobile && !showDesktopLessonChrome && styles.tabButtonMid,
                      isMobile && styles.tabButtonMobile,
                    ]}
                  >
                    <SubtitleText color="#0D0D0D">Materials</SubtitleText>
                    <View style={[styles.tabUnderline, { opacity: activeTab === 'materials' ? 1 : 0 }]} />
                  </TouchableOpacity>
                )}
                {showShortcutsTab && (
                  <TouchableOpacity
                    onPress={() => setActiveTab('shortcuts')}
                    style={[
                      styles.tabButton,
                      !isMobile && showDesktopLessonChrome && styles.tabButtonSidebar,
                      !isMobile && !showDesktopLessonChrome && styles.tabButtonMid,
                    ]}
                  >
                    <SubtitleText color="#0D0D0D">Shortcuts</SubtitleText>
                    <View style={[styles.tabUnderline, { opacity: activeTab === 'shortcuts' ? 1 : 0 }]} />
                  </TouchableOpacity>
                )}
              </XStack>

              <YStack mt={24} position="relative" w="100%">
                <YStack 
                  gap="$4" 
                  w="100%"
                  opacity={activeTab === 'overview' ? 1 : 0}
                  position={activeTab === 'overview' ? 'relative' : 'absolute'}
                  pointerEvents={activeTab === 'overview' ? 'auto' : 'none'}
                  style={{ 
                    left: 0, 
                    right: 0,
                    top: 0,
                    width: '100%',
                    zIndex: activeTab === 'overview' ? 1 : 0
                  }}
                >
                  <BodyText color="#2B2B2B" letterSpacing={0.25}>
                    {lessonSummary?.trim()?.length ? lessonSummary : 'No overview available.'}
                  </BodyText>
                </YStack>

                {showShortcutsTab && (
                  <ShortcutsTabContent isActive={activeTab === 'shortcuts'} />
                )}

                {hasLessonMaterials && (
                  <YStack 
                    gap="$6" 
                    w="100%"
                    opacity={activeTab === 'materials' ? 1 : 0}
                    position={activeTab === 'materials' ? 'relative' : 'absolute'}
                    pointerEvents={activeTab === 'materials' ? 'auto' : 'none'}
                    style={{ 
                      left: 0, 
                      right: 0,
                      top: 0,
                      width: '100%',
                      zIndex: activeTab === 'materials' ? 1 : 0
                    }}
                  >
                    {materials.map((m) => {
                      if (m.type === 'pdf') {
                        return (
                          <PdfMaterialItem
                            key={m.id}
                            material={m}
                            downloadingPdfId={downloadingPdfId}
                            onView={handlePdfView}
                            onDownload={handlePdfDownload}
                          />
                        );
                      }
                      if (
                        m.type === 'mp3' ||
                        m.type === 'm4a' ||
                        m.type === 'audio' ||
                        m.type === 'mp4'
                      ) {
                        return (
                          <AudioMaterialItem
                            key={m.id}
                            title={m.title?.trim() || m.url}
                            url={getProxiedMediaUrl(m.url)}
                            onDownload={() => handleAudioDownload(m.url, m)}
                            isDownloading={downloadingAudioId === m.id}
                            icon={renderMaterialIcon(m.type)}
                          />
                        );
                      }
                      return <LinkMaterialItem key={m.id} material={m} onPress={handleMaterialPress} />;
                    })}
                    <BodyText color="#5C5854">
                      Trouble accessing the materials? Make sure to deactivate ad blockers.
                    </BodyText>
                  </YStack>
                )}
              </YStack>
              
            </YStack>
          </YStack>
  );

  return (
    <View style={{ flex: 1, backgroundColor: '#F4F1ED', position: 'relative' }}>
      {/* Overlay (absolute) — only shows for allowlisted draft viewers on
          non-published lessons; never affects layout for anyone else. */}
      <StatusBadge status={lesson?.status} visible={canViewDrafts} overlay />
      {showDesktopLessonChrome ? (
        <XStack w="100%" f={1} ai="stretch" minWidth={0}>
          <LessonSidebarPanel expanded={sidebarExpanded}>
            <LessonCourseSidebar
              fillParent
              collapsed={!sidebarExpanded}
              onToggleCollapsed={toggleSidebarExpanded}
              loading={lessonSidebarQuery.isPending}
              lessons={lessonSidebarQuery.data?.lessons ?? []}
              courseMetadata={lessonSidebarQuery.data?.course?.metadata ?? null}
              activeLessonSlug={activeLessonSlug}
              currentVideoId={currentVideo?.id ?? null}
              currentTimeSeconds={currentTimeWholeSeconds}
              lessonVideosByLessonId={lessonVideosByLessonId}
              userVideosByLessonId={userVideosByLessonId}
              siblingLoadingLessonIds={siblingLoadingLessonIds}
              onSiblingExpand={handleSidebarSiblingExpand}
              onSelectLesson={handleSidebarSelectLesson}
              onSelectVideo={handleSidebarSelectVideo}
              onChapterSeek={handleSidebarChapterSeek}
              onChapterLinkClick={handleSidebarChapterLinkClick}
              onSelectLessonAtVideo={handleSidebarSelectLessonAtVideo}
              onSelectLessonAtChapter={handleSidebarSelectLessonAtChapter}
              headerTitle={sidebarHeaderTitle}
              seasonTitle={sidebarSeasonTitle}
              onSeasonPrev={handleSeasonPrev}
              onSeasonNext={handleSeasonNext}
              canPrevSeason={canPrevSeason}
              canNextSeason={canNextSeason}
            />
          </LessonSidebarPanel>
          <ScrollView
            ref={scrollViewRef}
            style={{ flex: 1, minWidth: 0 }}
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={Platform.OS === 'web'}
            bounces={false}
            refreshControl={<RefreshControl refreshing={false} />}
          >
            <YStack w="100%" px={16} pt={SIDEBAR_SHELL_PADDING_TOP}>
              <LessonPageTopBar
                courseTitle={(courseTitle as string) || null}
                courseSlug={String(courseSlug)}
                seasonTitle={lessonSidebarQuery.data?.current_season?.title ?? null}
                lessonTitle={displayLessonTitle}
                canNavigatePrevious={hasPrevNavigation}
                canNavigateNext={hasNextNavigation}
                isCompleted={isCurrentVideoCompleted}
                completionLoading={completionLoading}
                isFiftyPercentWatched={isFiftyPercentWatched}
                onMarkComplete={handleMarkComplete}
                notationMode={notationMode}
                hasNotation={hasNotation}
                onNotationChange={handleNotationChange}
                onPreviousPress={handlePreviousLesson}
                onNextPress={handleNextLesson}
              />
              {lessonMainColumn}
            </YStack>
          </ScrollView>
        </XStack>
      ) : (
      <ScrollView
        ref={scrollViewRef}
        style={{ flex: 1 }}
        scrollEnabled={!focusActive}
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: focusActive
            ? 0
            : !showDesktopLessonChrome
              ? showCourseLessonsBottomInset + 64
              : 0,
        }}
        showsVerticalScrollIndicator={Platform.OS === "web"}
        bounces={false}
        refreshControl={<RefreshControl refreshing={false} />}
      >
        <YStack w="100%">
          <ContentWrapper
            maxWidth={1140}
            px={16}
            py={focusActive ? 0 : '$4'}
          >
          <YStack>
            {!showDesktopLessonChrome && !focusActive ? (
              <YStack gap="$1">
                <BreadcrumbHeader
                  insetX={0}
                  courseTitle={(courseTitle as string) || undefined}
                  courseSlug={courseSlug}
                  seasonTitle={lessonSidebarQuery.data?.current_season?.title ?? undefined}
                  referrer={referrer}
                  collectionTitle={(referrer && referrerTitle as string) || undefined}
                />
                <XStack ai="center" gap="$2" py="$1">
                  <Pressable
                    disabled={!mobPrevEnabled}
                    onPress={handlePreviousLesson}
                    onHoverIn={() => mobPrevEnabled && setMobNavPrevHover(true)}
                    onHoverOut={() => setMobNavPrevHover(false)}
                    onPressIn={() => mobPrevEnabled && setMobNavPrevPress(true)}
                    onPressOut={() => setMobNavPrevPress(false)}
                    style={lessonNavCompactSurface(mobPrevEnabled, mobPrevElevated)}
                  >
                    <Ionicons
                      name="chevron-back"
                      size={20}
                      color={lessonNavForeground(mobPrevEnabled, mobPrevElevated)}
                    />
                  </Pressable>

                  <XStack flex={1} jc="center">
                    <MarkCompleteButton
                      isCompleted={isCurrentVideoCompleted}
                      isLoading={completionLoading}
                      isFiftyPercentWatched={isFiftyPercentWatched}
                      onPress={handleMarkComplete}
                      isMobile
                      isActive
                      variant="toolbar"
                    />
                  </XStack>

                  <Pressable
                    disabled={!mobNextEnabled}
                    onPress={handleNextLesson}
                    onHoverIn={() => mobNextEnabled && setMobNavNextHover(true)}
                    onHoverOut={() => setMobNavNextHover(false)}
                    onPressIn={() => mobNextEnabled && setMobNavNextPress(true)}
                    onPressOut={() => setMobNavNextPress(false)}
                    style={lessonNavCompactSurface(mobNextEnabled, mobNextElevated)}
                  >
                    <Ionicons
                      name="chevron-forward"
                      size={20}
                      color={lessonNavForeground(mobNextEnabled, mobNextElevated)}
                    />
                  </Pressable>

                  {localPlayback ? (
                    offlineRecord?.hasNotation ? (
                      <Text style={styles.offlineNotationHint}>Notation available online</Text>
                    ) : null
                  ) : (
                    <VideoActionButtons
                      notationMode={notationMode}
                      hasNotation={hasNotation}
                      onNotationChange={handleNotationChange}
                      toolbarCompact
                    />
                  )}
                </XStack>
              </YStack>
            ) : null}

            {lessonMainColumn}
          </YStack>


          </ContentWrapper>
        </YStack>
      </ScrollView>
      )}

      {!showDesktopLessonChrome && !focusActive && (
        <Pressable
          onPress={() => setSidebarSheetOpen(true)}
          accessibilityRole="button"
          accessibilityLabel={
            lessonSidebarQuery.data?.current_season?.title
              ? `Show ${lessonSidebarQuery.data.current_season.title}`
              : 'Show course lessons'
          }
          style={({ pressed }) => ({
            position: 'absolute',
            bottom: showCourseLessonsBottomInset,
            right: 20,
            width: 52,
            height: 52,
            borderRadius: 26,
            zIndex: 10,
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 1,
            borderColor: '#E0DED8',
            backgroundColor: pressed ? '#F4F1ED' : '#FAFAF8',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 3 },
            shadowOpacity: 0.12,
            shadowRadius: 10,
            elevation: 4,
          })}
        >
          <Ionicons name="list" size={26} color="#3A3633" />
        </Pressable>
      )}

          <LessonCompletionModal
            open={!isMobile && completionModalOpen}
            suggestions={suggestedLessons}
            onOpenChange={(open) => {
              if (open === completionModalOpen) return;
              setCompletionModalOpen(open);
              if (open) setCompletionSheetOpen(false);
            }}
            onBack={handleCompletionBack}
            courseName={courseTitle as string}
            onSuggestionPress={handleSuggestionPress}
          />

          <LessonCompletionSheet
            open={isMobile && completionSheetOpen}
            suggestions={suggestedLessons}
            onOpenChange={(open) => {
              if (open === completionSheetOpen) return;
              setCompletionSheetOpen(open);
              if (open) setCompletionModalOpen(false);
            }}
            onBack={handleCompletionBack}
            courseName={courseTitle as string}
            onSuggestionPress={handleSuggestionPress}
          />

          <Sheet
            open={sidebarSheetOpen}
            onOpenChange={setSidebarSheetOpen}
            snapPoints={sidebarSheetSnapPoints}
          >
            <SheetDragHandle />
            <LessonCourseSidebar
              fillParent
              loading={lessonSidebarQuery.isPending}
              lessons={lessonSidebarQuery.data?.lessons ?? []}
              courseMetadata={lessonSidebarQuery.data?.course?.metadata ?? null}
              activeLessonSlug={activeLessonSlug}
              currentVideoId={currentVideo?.id ?? null}
              currentTimeSeconds={currentTimeWholeSeconds}
              lessonVideosByLessonId={lessonVideosByLessonId}
              userVideosByLessonId={userVideosByLessonId}
              siblingLoadingLessonIds={siblingLoadingLessonIds}
              onSiblingExpand={handleSidebarSiblingExpand}
              onSelectLesson={handleMobileSelectLesson}
              onSelectVideo={handleSidebarSelectVideo}
              onChapterSeek={handleSidebarChapterSeek}
              onChapterLinkClick={handleSidebarChapterLinkClick}
              onSelectLessonAtVideo={handleMobileSelectLessonAtVideo}
              onSelectLessonAtChapter={handleMobileSelectLessonAtChapter}
              headerTitle={sidebarHeaderTitle}
              seasonTitle={sidebarSeasonTitle}
              onSeasonPrev={handleSeasonPrev}
              onSeasonNext={handleSeasonNext}
              canPrevSeason={canPrevSeason}
              canNextSeason={canNextSeason}
              ScrollViewComponent={SheetScrollView as any}
            />
          </Sheet>

          <LessonShareModal
            open={shareModalOpen}
            onOpenChange={setShareModalOpen}
            shareUrl={lessonShareUrl}
            shareAtCurrentTime={shareAtCurrentTime}
            onShareAtCurrentTimeChange={setShareAtCurrentTime}
            onCopied={handleShareModalCopied}
            onCopyError={handleShareModalCopyError}
          />

      <PdfViewerModal
        url={viewingPdf?.url ?? null}
        title={viewingPdf?.title}
        onClose={closePdfViewer}
      />

      <CopyLinkToast feedback={copyLinkFeedback} bottomInset={insets.bottom} />
    </View>
  );
}

function CopyLinkToast({
  feedback,
  bottomInset,
}: {
  feedback: 'idle' | 'copied' | 'shared' | 'error';
  bottomInset: number;
}) {
  if (feedback === 'idle') return null;
  const label =
    feedback === 'shared' ? 'Shared' : feedback === 'copied' ? 'Copied' : 'Could not copy';
  const color = feedback === 'error' ? '#FFCDD2' : '#FFFFFF';
  return (
    <View
      pointerEvents="none"
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: Math.max(bottomInset, COPY_TOAST_MIN_SAFE_INSET) + COPY_TOAST_BOTTOM_OFFSET,
        alignItems: 'center',
        zIndex: COPY_TOAST_Z_INDEX,
      }}
    >
      <View
        style={{
          backgroundColor: 'rgba(27, 27, 27, 0.94)',
          paddingHorizontal: 22,
          paddingVertical: 12,
          borderRadius: 10,
          maxWidth: '90%',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.28,
          shadowRadius: 10,
          elevation: 10,
        }}
      >
        <Text
          style={{
            color,
            fontSize: 15,
            fontWeight: '600',
            textAlign: 'center',
          }}
        >
          {label}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  offlineUnavailable: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: '#1B1B1B',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    gap: 10,
  },
  offlineUnavailableText: {
    color: '#FFFFFF',
    fontSize: 15,
    lineHeight: 21,
    textAlign: 'center',
  },
  offlineNotationHint: {
    color: '#5C5854',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 8,
  },
  playbackChoiceRow: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 4,
  },
  playbackChoiceButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 22,
    backgroundColor: '#FFFFFF',
  },
  playbackChoiceButtonText: {
    color: '#0D0D0D',
    fontSize: 14,
    fontWeight: '600',
  },
  playbackSwitchLabel: {
    color: '#5C5854',
    fontSize: 12,
    fontWeight: '600',
  },
  playbackSwitchLink: {
    color: '#0D0D0D',
    fontSize: 12,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  playerCard: {
    width: '100%',
    maxWidth: '100%',
    aspectRatio: 16 / 9,
    overflow: 'hidden',
    backgroundColor: '#FFF',
    borderRadius: 8,
  },
  loadingOverlay: { ...StyleSheet.absoluteFillObject, zIndex: 2, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', alignItems: 'center' },
  loadingText: { color: '#fff' },
  fullscreenButton: {
    position: 'absolute',
    top: 10,
    left: 10,
    zIndex: 3,
    width: 38,
    height: 38,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(13,13,13,0.6)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.18)',
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 4,
  },
  fullscreenButtonPressed: {
    backgroundColor: 'rgba(13,13,13,0.82)',
    opacity: 0.9,
  },
  tabsContainer: { borderBottomWidth: 1, borderBottomColor: '#D6D3CE', marginTop: 12, marginBottom: 12 },
  tabsContainerMobile: { marginTop: 8, marginBottom: 8 },
  tabButton: { paddingHorizontal: 24, paddingBottom: 12, position: 'relative' },
  tabButtonMobile: { paddingHorizontal: 12, paddingBottom: 10 },
  tabButtonMid: { paddingHorizontal: 16, paddingBottom: 12 },
  tabButtonSidebar: { paddingHorizontal: 12, paddingBottom: 12 },
  tabUnderline: { position: 'absolute', bottom: 0, left: 0, right: 0, height: 4, backgroundColor: '#C9C5C3' },
});